import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    updateDoc,
    where,
    orderBy,
    Timestamp,
    onSnapshot
} from 'firebase/firestore';
import { TableKiosk, tablesKiosksRef } from '@api/TablesKiosks/TableKiosk';

export default {
    async saveTableKiosk(tableKiosk: TableKiosk): Promise<TableKiosk> {
        const timestamp = Timestamp.now();
        const tableKioskData = {
            ...tableKiosk,
            updatedAt: timestamp,
            createdAt: tableKiosk.id ? tableKiosk.createdAt : timestamp
        };

        if (!tableKiosk.id) {
            const data = await addDoc(tablesKiosksRef, tableKioskData);
            tableKiosk.id = data.id;
            await updateDoc(doc(tablesKiosksRef, data.id), { id: data.id });
            return { ...tableKioskData, id: data.id };
        }

        await updateDoc(doc(tablesKiosksRef, tableKiosk.id), tableKioskData);
        return { ...tableKioskData, id: tableKiosk.id };
    },

    async getTablesKiosks(branchOfficeId?: string): Promise<TableKiosk[]> {
        const tablesKiosks: TableKiosk[] = [];
        let queryDoc = query(tablesKiosksRef, orderBy('createdAt', 'desc'));

        if (branchOfficeId) {
            queryDoc = query(
                tablesKiosksRef,
                where('branchOfficeId', '==', branchOfficeId),
                orderBy('createdAt', 'desc')
            );
        }

        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return tablesKiosks;
        }

        snapshot.docs.forEach((doc) => {
            const data = doc.data();
            tablesKiosks.push({
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate(),
                updatedAt: data.updatedAt?.toDate()
            } as TableKiosk);
        });

        return tablesKiosks;
    },

    getTablesKiosksRealtime(branchOfficeId: string, callback: (tablesKiosks: TableKiosk[]) => void): () => void {
        const q = query(tablesKiosksRef, where('branchOfficeId', '==', branchOfficeId), orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const tablesKiosks: TableKiosk[] = [];

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    tablesKiosks.push({
                        id: doc.id,
                        ...data,
                        createdAt: data.createdAt?.toDate(),
                        updatedAt: data.updatedAt?.toDate()
                    } as TableKiosk);
                });

                callback(tablesKiosks);
            },
            (error) => {
                console.error('Error en tiempo real de mesas/kiosks:', error);
            }
        );

        return unsubscribe;
    },

    async getTableKiosk(tableKioskId: string): Promise<TableKiosk> {
        const documentData = await getDoc(doc(tablesKiosksRef, tableKioskId));
        const data = documentData.data();
        return {
            id: documentData.id,
            ...data,
            createdAt: data?.createdAt?.toDate(),
            updatedAt: data?.updatedAt?.toDate()
        } as TableKiosk;
    },

    async deleteTableKiosk(tableKioskId: string): Promise<void> {
        await deleteDoc(doc(tablesKiosksRef, tableKioskId));
    },

    async toggleStatus(tableKioskId: string): Promise<void> {
        const tableKiosk = await this.getTableKiosk(tableKioskId);
        await updateDoc(doc(tablesKiosksRef, tableKioskId), {
            status: !tableKiosk.status,
            updatedAt: Timestamp.now()
        });
    },

    async updateTableStatus(tableId: string, status: string): Promise<void> {
        await updateDoc(doc(tablesKiosksRef, tableId), {
            tableStatus: status,
            updatedAt: Timestamp.now()
        });
    },

    async updateKioskStatus(kioskId: string, status: string): Promise<void> {
        await updateDoc(doc(tablesKiosksRef, kioskId), {
            kioskStatus: status,
            updatedAt: Timestamp.now()
        });
    }
};
